import { observer } from "mobx-react";
import { History } from "history";
import { getBackUrlFromHistory } from "./meetingsDomain";
import { TFunction, TMessage } from "@/i18n/tt";
import { FC, ReactElement, useEffect, useMemo } from "react";
import {
  MatterView,
  NoticeOfBoardMemberView,
} from "@/noticeForBoardMember/noticeForBoardmemberComponents";
import { isOk, Result } from "@/common/common";
import {
  createActionsProps,
  createEditComment,
  MatterForReview,
  MeetingEmailController,
  MeetingEmailInitializerController,
} from "@/noticeForBoardMember/noticeForBoardMemberDomain";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Alert } from "@/components-v2/Alert";
import { LoggedOnUserInfoDTO } from "@/types/UserInfo";
import AlertMessageStore from "@/stores/AlertMessageStore";
import {
  useDispatch,
  useMessageStore,
  useRootStore,
  useTranslation,
  useUserStore,
} from "@/stores/RootStoreContext";

const ReviewMinutesInitializer: FC<{ meetingEmailId: string; matterId?: string }> = observer(
  ({ meetingEmailId, matterId }) => {
    const { t } = useTranslation();
    const { http } = useRootStore();
    const dispatch = useDispatch();
    const messageStore = useMessageStore();
    const userStore = useUserStore();
    const history = useRootStore().history;
    const ctrl = useMemo(
      () =>
        new MeetingEmailInitializerController(dispatch, http, (userInfo: LoggedOnUserInfoDTO) => {
          userStore.setUserInfo(userInfo);
        }),
      [dispatch, http, userStore],
    );
    useEffect(() => {
      if (meetingEmailId && matterId) {
        ctrl.fetchMatter(meetingEmailId, matterId);
      } else if (meetingEmailId) {
        ctrl.fetchDocumentFromMeetingEmail(meetingEmailId);
      }
    }, [ctrl, meetingEmailId, matterId]);

    if (matterId && isOk(ctrl.result)) {
      return renderMatter(
        messageStore,
        t,
        ctrl.result.data,
        history,
        ctrl.result.data.matterResult,
      );
    } else {
      return render(t, ctrl.result, history);
    }
  },
);

function render(
  t: TFunction,
  result: Result<MeetingEmailController, TMessage>,
  history: History,
): ReactElement {
  switch (result.state) {
    case "pending":
      return <LoadingIndicator />;
    case "failure":
      return <Alert type={"error"} message={t(result.error)} />;
    case "cancelled":
      return <div />;
    case "ok": {
      const ctrl = result.data;
      const reviewMinutesData = ctrl.response;
      return (
        <NoticeOfBoardMemberView
          backUrl={getBackUrlFromHistory(history)}
          data={reviewMinutesData}
          onDownloadAttachment={ctrl.openPreviewAttachmentDialog}
          onCloseAttachmentPreview={ctrl.closePreviewAttachmentDialog}
          previewAttachmentState={ctrl.previewState}
          actions={createActionsProps(reviewMinutesData, ctrl, history)}
          editComment={createEditComment(ctrl)}
          isReviewMinutes
          downloadNotice={ctrl.downloadNotice}
        />
      );
    }
  }
}

function renderMatter(
  messageStore: AlertMessageStore,
  t: TFunction,
  controller: MeetingEmailController,
  history: History,
  matterResult?: Result<MatterForReview, TMessage>,
): ReactElement {
  switch (matterResult?.state) {
    case undefined:
    case "pending":
      return <LoadingIndicator />;
    case "failure":
      return <Alert type={"error"} message={t(matterResult.error)} />;
    case "cancelled":
      return <div />;
    case "ok": {
      const actions = createActionsProps(controller.response, controller, history);
      return (
        <MatterView
          matter={matterResult.data}
          companyroleId={controller.response.companyroleId}
          onDownloadAttachment={actions?.onDownloadAttachment}
          messageStore={messageStore}
          resetVote={controller.hasVoted ? controller.resetVote : undefined}
          editComment={createEditComment(controller)}
          {...actions}
        />
      );
    }
  }
}

export default ReviewMinutesInitializer;
